import { useState, useRef, useCallback } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Input, Modal, ModalFooter, Spinner, ErrorMsg, Card, Badge, useToast, PageHeader, EmptyState, FieldLabel, Icon } from '../components/ui';

// ---------------------------------------------------------------------------
// LinkedDealsBadge — the "# links" badge that reveals the linked deals (as
// in-app hyperlinks) on hover. Links are fetched lazily on first hover and
// cached. Clicking a deal navigates in-app via the shared `harvi-navigate`
// event (no full page reload); detail-level links open their parent deal.
// ---------------------------------------------------------------------------
const LinkedDealsBadge = ({ contactId, linkCount }) => {
  const [open,    setOpen]    = useState(false);
  const [links,   setLinks]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const fetchedRef = useRef(false);

  const loadLinks = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    try {
      const rows = await api.get(`contacts/${contactId}/links`);
      setLinks(rows || []);
    } catch (e) {
      setError(e.message);
      fetchedRef.current = false; // allow a retry on next hover
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  const show = () => { setOpen(true); loadLinks(); };
  const hide = () => setOpen(false);

  const goToDeal = (e, dealId) => {
    if (!dealId) return;
    e.preventDefault();
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('harvi-navigate', { detail: { view: 'deals', deal: dealId } }));
  };

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={e => e.stopPropagation()}
    >
      <Badge color={B.tealDk}>{linkCount} link{linkCount > 1 ? 's' : ''}</Badge>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
          minWidth: 220, maxWidth: 320, maxHeight: 280, overflowY: 'auto',
          background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10,
          boxShadow: B.shadowLg || '0 8px 24px rgba(0,0,0,0.14)', padding: '6px 0',
          cursor: 'default', textAlign: 'left',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: B.textLt, textTransform: 'uppercase', letterSpacing: 0.4, padding: '4px 12px 6px' }}>
            Linked Deals
          </div>
          {loading && <div style={{ fontSize: 12, color: B.textMd, padding: '4px 12px' }}>Loading…</div>}
          {error && !loading && <div style={{ fontSize: 12, color: B.red, padding: '4px 12px' }}>{error}</div>}
          {!loading && !error && (links || []).length === 0 && (
            <div style={{ fontSize: 12, color: B.textLt, padding: '4px 12px' }}>No linked deals.</div>
          )}
          {!loading && !error && (links || []).map(l => (
            <a
              key={l.linkID}
              href={`/?view=deals&deal=${l.dealID}`}
              onClick={e => goToDeal(e, l.dealID)}
              style={{
                display: 'block', padding: '7px 12px', fontSize: 13, color: B.navy,
                textDecoration: 'none', fontWeight: 600, lineHeight: 1.3,
              }}
              onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {l.dealName || 'Untitled Deal'}
              {l.dealDetailID && l.detailName && (
                <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: B.textMd, marginTop: 1 }}>
                  {l.detailName}
                </span>
              )}
            </a>
          ))}
        </div>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// PersonsPanel — expandable list of persons under a company
// ---------------------------------------------------------------------------
const PersonsPanel = ({ contactId, primaryDetailId, onPrimaryChanged }) => {
  const { toast } = useToast();
  const { data: persons, loading, reload } = useApi(
    () => api.get(`contacts/${contactId}/persons`),
    [contactId]
  );
  const [showNew, setShowNew]   = useState(false);
  const [newForm, setNewForm]   = useState({ name: '', phone: '', email: '', title: '' });
  const [saving,  setSaving]    = useState(false);
  const [editId,  setEditId]    = useState(null);
  const [editFrm, setEditFrm]   = useState(null);
  const [eSaving, setESaving]   = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const createPerson = async () => {
    if (!newForm.name.trim()) return;
    setSaving(true);
    try {
      await api.post(`contacts/${contactId}/persons`, newForm);
      setNewForm({ name: '', phone: '', email: '', title: '' });
      setShowNew(false);
      reload();
      if (onPrimaryChanged) onPrimaryChanged();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (p) => {
    setEditId(p.contactDetailID);
    setEditFrm({ name: p.name || '', phone: p.phone || '', email: p.email || '', title: p.title || '' });
  };

  const saveEdit = async () => {
    setESaving(true);
    try {
      await api.put(`contacts/${contactId}/persons/${editId}`, editFrm);
      setEditId(null);
      reload();
      if (onPrimaryChanged) onPrimaryChanged();
    } catch (e) { toast.error(e.message); }
    finally { setESaving(false); }
  };

  const deletePerson = (pid) => {
    setConfirmDel({
      message: 'Remove this person?',
      onConfirm: async () => {
        setEditId(null);
        try {
          await api.del(`contacts/${contactId}/persons/${pid}`);
          reload();
          if (onPrimaryChanged) onPrimaryChanged();
        } catch (e) { toast.error(e.message); }
      },
    });
  };

  const setPrimary = async (pid) => {
    try {
      await api.put(`contacts/${contactId}/persons/${pid}`, { isPrimary: true });
      setEditId(null);
      reload();
      if (onPrimaryChanged) onPrimaryChanged();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ background: B.surfaceMuted, borderTop: `1px solid ${B.border}` }}>
      {(persons || []).map(p => (
        <div key={p.contactDetailID} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px 10px 42px', borderBottom: `1px solid ${B.border}`,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13 }}>
              {p.name}
              {p.contactDetailID === primaryDetailId && (
                <Badge color={B.navy}>Primary</Badge>
              )}
            </div>
            {(p.title || p.email || p.phone) && (
              <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
                {[p.title, p.email, p.phone].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <Btn size="sm" variant="ghost" onClick={() => startEdit(p)}>Edit</Btn>
        </div>
      ))}

      {(persons || []).length === 0 && !showNew && (
        <div style={{ fontSize: 12, color: B.textLt, padding: '10px 42px' }}>No persons yet.</div>
      )}

      {showNew ? (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${B.border}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <Input label="Name *" value={newForm.name}  onChange={v => setNewForm(p => ({ ...p, name: v }))} />
            <Input label="Title"  value={newForm.title} onChange={v => setNewForm(p => ({ ...p, title: v }))} />
            <Input label="Email" type="email" value={newForm.email} onChange={v => setNewForm(p => ({ ...p, email: v }))} />
            <Input label="Phone" type="tel"   value={newForm.phone} onChange={v => setNewForm(p => ({ ...p, phone: v }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" variant="ghost" onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn size="sm" disabled={!newForm.name.trim() || saving} onClick={createPerson}>{saving ? 'Saving…' : 'Add Person'}</Btn>
          </div>
        </div>
      ) : (
        <div style={{ padding: '8px 16px 8px 42px' }}>
          <Btn size="sm" variant="ghost" onClick={() => setShowNew(true)}>+ Add Person</Btn>
        </div>
      )}

      {editId && editFrm && (
        <Modal title="Edit Person" onClose={() => setEditId(null)}>
          <Input label="Name *"  value={editFrm.name}  onChange={v => setEditFrm(p => ({ ...p, name: v }))} />
          <Input label="Title"   value={editFrm.title} onChange={v => setEditFrm(p => ({ ...p, title: v }))} />
          <Input label="Email" type="email" value={editFrm.email} onChange={v => setEditFrm(p => ({ ...p, email: v }))} />
          <Input label="Phone" type="tel"   value={editFrm.phone} onChange={v => setEditFrm(p => ({ ...p, phone: v }))} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {editId !== primaryDetailId && (
                <Btn variant="ghost" onClick={() => setPrimary(editId)}>Set as Primary</Btn>
              )}
              <Btn variant="ghost" style={{ color: B.red, border: `1px solid ${B.red}44` }} onClick={() => deletePerson(editId)}>Delete</Btn>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="ghost" onClick={() => setEditId(null)}>Cancel</Btn>
              <Btn disabled={!editFrm.name.trim() || eSaving} onClick={saveEdit}>{eSaving ? 'Saving…' : 'Save'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmDel && (
        <Modal title="Confirm" onClose={() => setConfirmDel(null)}>
          <div style={{ fontSize: 14, color: B.text, marginBottom: 20 }}>{confirmDel.message}</div>
          <ModalFooter>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={() => { const fn = confirmDel.onConfirm; setConfirmDel(null); fn(); }}>Delete</Btn>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Contacts page
// ---------------------------------------------------------------------------
export const Contacts = () => {
  const { toast } = useToast();
  const { data: companies, loading, error, reload } = useApi(() => api.get('contacts'), []);
  const [expanded,  setExpanded]  = useState(new Set());
  const [showNew,   setShowNew]   = useState(false);
  const [newForm,   setNewForm]   = useState({ companyName: '', primaryPerson: { name: '', email: '', phone: '', title: '' } });
  const [saving,    setSaving]    = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [editForm,  setEditForm]  = useState(null);
  const [editSaving,setEditSaving]= useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const createCompany = async () => {
    if (!newForm.companyName.trim()) return;
    setSaving(true);
    try {
      await api.post('contacts', {
        companyName:   newForm.companyName.trim(),
        primaryPerson: newForm.primaryPerson.name?.trim() ? newForm.primaryPerson : null,
      });
      setNewForm({ companyName: '', primaryPerson: { name: '', email: '', phone: '', title: '' } });
      setShowNew(false);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditId(c.contactID);
    setEditForm({ companyName: c.companyName || '' });
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`contacts/${editId}`, { companyName: editForm.companyName });
      setEditId(null);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setEditSaving(false); }
  };

  const deleteCompany = (id, name) => {
    setConfirmDel({
      message: `Delete company "${name}"? This will also remove all linked contacts.`,
      onConfirm: async () => {
        try { await api.del(`contacts/${id}`); reload(); }
        catch (e) { toast.error(e.message); }
      },
    });
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  return (
    <div>
      <PageHeader
        icon="contacts"
        title="Contacts"
        actions={<Btn onClick={() => setShowNew(v => !v)}>{showNew ? 'Cancel' : <><Icon name="add" size={15} /> New Company</>}</Btn>}
      />

      {showNew && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>New Company</div>
          <Input label="Company Name *" value={newForm.companyName} onChange={v => setNewForm(p => ({ ...p, companyName: v }))} />
          <div style={{ margin: '12px 0 6px' }}><FieldLabel>Primary Contact (optional)</FieldLabel></div>
          <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Name"  value={newForm.primaryPerson.name}  onChange={v => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, name: v } }))} />
            <Input label="Title" value={newForm.primaryPerson.title} onChange={v => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, title: v } }))} />
            <Input label="Email" type="email" value={newForm.primaryPerson.email} onChange={v => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, email: v } }))} />
            <Input label="Phone" type="tel"   value={newForm.primaryPerson.phone} onChange={v => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, phone: v } }))} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn disabled={!newForm.companyName.trim()} loading={saving} onClick={createCompany}>Create</Btn>
          </div>
        </Card>
      )}

      <Card style={{ padding: 0 }}>
        {(companies || []).length === 0 && (
          <EmptyState
            icon="contacts"
            title="No companies yet"
            message="Add companies and their contacts to keep deal relationships in one place."
            action={!showNew ? <Btn onClick={() => setShowNew(true)}><Icon name="add" size={15} /> New Company</Btn> : null}
          />
        )}
        {(companies || []).map(c => {
          const isOpen = expanded.has(c.contactID);
          return (
            <div key={c.contactID} style={{ borderBottom: `1px solid ${B.border}` }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer' }}
                onClick={() => toggle(c.contactID)}
                onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: B.textLt, display: 'flex', width: 14 }}><Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={14} /></span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{c.companyName}</div>
                    {c.primaryPersonName && (
                      <div style={{ fontSize: 12, color: B.textMd }}>
                        {c.primaryPersonName}{c.primaryPersonTitle ? ` · ${c.primaryPersonTitle}` : ''}{c.primaryPersonEmail ? ` · ${c.primaryPersonEmail}` : ''}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {(c.personCount || 0) > 0 && (
                    <Badge color={B.textMd}>{c.personCount} person{c.personCount > 1 ? 's' : ''}</Badge>
                  )}
                  {(c.linkCount || 0) > 0 && (
                    <LinkedDealsBadge contactId={c.contactID} linkCount={c.linkCount} />
                  )}
                  <Btn size="sm" variant="ghost" onClick={e => { e.stopPropagation(); startEdit(c); }}>Edit</Btn>
                  <Btn size="sm" variant="ghost" style={{ color: B.red, border: `1px solid ${B.red}44` }}
                    onClick={e => { e.stopPropagation(); deleteCompany(c.contactID, c.companyName); }}>
                    Delete
                  </Btn>
                </div>
              </div>
              {isOpen && (
                <PersonsPanel
                  contactId={c.contactID}
                  primaryDetailId={c.primaryContactDetailID}
                  onPrimaryChanged={reload}
                />
              )}
            </div>
          );
        })}
      </Card>

      {editId && editForm && (
        <Modal title="Edit Company" onClose={() => setEditId(null)}>
          <Input label="Company Name *" value={editForm.companyName} onChange={v => setEditForm(p => ({ ...p, companyName: v }))} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setEditId(null)}>Cancel</Btn>
            <Btn disabled={!editForm.companyName.trim() || editSaving} onClick={saveEdit}>{editSaving ? 'Saving…' : 'Save'}</Btn>
          </div>
        </Modal>
      )}

      {confirmDel && (
        <Modal title="Confirm Delete" onClose={() => setConfirmDel(null)}>
          <div style={{ fontSize: 14, color: B.text, marginBottom: 20 }}>{confirmDel.message}</div>
          <ModalFooter>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={() => { const fn = confirmDel.onConfirm; setConfirmDel(null); fn(); }}>Delete</Btn>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};
